import { Component, computed, input } from '@angular/core';
import { FormField } from '@angular/forms/signals';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatError } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SeriesWeekday } from '@app/shared/types/models/reports/time-report-series';
import { UiChoiceGroupComponent } from '@app/shared/ui/choice-group/ui-choice-group.component';
import { UiDateRangePickerComponent } from '@app/shared/ui/date-range-picker/ui-date-range-picker.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { ProffeoUiChoiceOption } from '@app/shared/ui/types/choice-group.types';
import { SERIES_BREAK_PRESETS, SERIES_WEEKDAY_OPTIONS } from '@app/shared/utils/time-report-series.utils';
import { TranslatePipe } from '@ngx-translate/core';
import { ReportSeriesForm } from './report-series-form';

/**
 * ReportSeriesSectionComponent - The one series UI shared by the work-time and absence dialogs:
 * opt-in toggle, series date range, weekday chips and (Range mode) the optional break.
 *
 * Renders nothing when the series is unavailable (production or non-test API, edit mode).
 *
 * @selector proffeo-report-series-section
 */
@Component({
  selector: 'proffeo-report-series-section',
  templateUrl: './report-series-section.component.html',
  styleUrl: './report-series-section.component.css',
  imports: [
    FormField,
    MatCheckboxModule,
    MatError,
    MatSlideToggleModule,
    TranslatePipe,
    UiChoiceGroupComponent,
    UiDateRangePickerComponent,
    UiInputComponent
  ]
})
export class ReportSeriesSectionComponent {
  protected readonly weekdayOptions = SERIES_WEEKDAY_OPTIONS;
  protected readonly breakOptions: readonly ProffeoUiChoiceOption<number>[] = SERIES_BREAK_PRESETS.map(minutes => ({
    value: minutes,
    label: `${minutes} min`
  }));
  protected readonly selectedBreakPreset = computed((): number | null => {
    const minutes = this.series().model().breakMinutes;
    return SERIES_BREAK_PRESETS.some(preset => preset === minutes) ? minutes : null;
  });

  /** @summary Shared series state created by the hosting dialog. */
  public readonly series = input.required<ReportSeriesForm>();

  /** @summary Shows the "attachments go to the first day" note (work-time dialog). */
  public readonly attachmentsNote = input(false);

  protected isWeekend(weekday: SeriesWeekday): 'saturday' | 'sunday' | undefined {
    if (weekday === SeriesWeekday.Saturday) {
      return 'saturday';
    }
    return weekday === SeriesWeekday.Sunday ? 'sunday' : undefined;
  }

  protected onBreakPreset(minutes: number | null): void {
    if (minutes != null) {
      this.series().setBreakMinutes(minutes);
    }
  }

  protected onBreakInput(value: unknown): void {
    const minutes = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10);
    this.series().setBreakMinutes(Number.isFinite(minutes) ? minutes : 0);
  }
}
